import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import dayjs from 'dayjs';
import { App as AntApp, Input, Segmented } from 'antd';
import SheetDate from '../components/SheetDate';
import AppBar from '../components/AppBar';
import DocViewer from '../components/DocViewer';
import { AsyncButton } from '../components/kit';
import { AutosaveStatus, RestoredDraftBanner } from '../components/AutosaveStatus';
import { useUnsavedChanges } from '../context/UnsavedChangesContext';
import { useAuth } from '../context/AuthContext';
import useFormAutosave from '../hooks/useFormAutosave';
import SheetSelect from '../components/SheetSelect';
import PartyQuickCreate from '../components/PartyQuickCreate';
import { InventorySection, OwnerLouSection } from '../components/AgreementExtras';
import usePagedOptions from '../hooks/usePagedOptions';
import * as bookingFormsApi from '../api/bookingForms';
import * as contactsApi from '../api/contacts';
import {
  SALES_MONEY_TERMS, RENT_MONEY_TERMS, moneyTerms, basisKey, amountKey, derivedKey,
  derivedBasis, moneyBasis, moneyTermField, defaultMoneyBasis, pruneMoneyTerms,
  moneyUnitOptions, saleTypeOfCode, MAX_EXTRA_PAYMENTS, EXTRA_PAYMENT_LABEL_MAX,
  blankExtraPayment, extraPayments,
} from '../lib/bookingFormMoney';
import { CODE_LABELS, CODE_OPTS, isAuthCode, RENTAL_SST_OPTIONS, RENTAL_SST_EXCLUSIVE, RENTAL_SST_INCLUSIVE } from '../lib/bookingFormTypes';
import { lockedEditHint } from '../lib/formRevisions';

// FULL-SCREEN · mounted at /forms/new (create) and /forms/:id/edit (edit).
// One page, six form codes. Everything the agent fills lives in a single flat
// `form_data` object keyed exactly like the admin BookingFormPage so the same
// server template renders it. On create the agent first picks the code; on edit
// the code is fixed and we prefill from the saved form.

const FURNISH_OPTS = ['Unfurnished', 'Partial Furnished', 'Fully Furnished'].map((v) => ({ value: v, label: v }));
const BASIS_OPTS = ['Sole Agency', 'Joint Agency', 'Ad-hoc Agency'].map((v) => ({ value: v, label: v }));
const CAPACITY_OPTS = [
  { value: 'registered proprietor', label: 'Registered Proprietor' },
  { value: 'duly authorised representative', label: 'Authorised Representative (POA)' },
  { value: 'trustee', label: 'Trustee' },
];
const PERIOD_TYPE_OPTS = [{ label: 'Days', value: 'days' }, { label: 'Months', value: 'month' }];
const VACANT_TYPE_OPTS = [{ label: 'Vacant', value: 'vacant' }, { label: 'Existing tenancy', value: 'existing_tenancy' }];
// ALS prints "{{#if vacant_possession}}YES{{else}}NO{{/if}}" — a widget with a
// default would decide this for the owner, so it is a deliberate YES/NO pick.
const VACANT_YN_OPTS = [{ label: 'YES', value: true }, { label: 'NO', value: false }];
const TERMINATION_OPTS = [{ label: 'No termination', value: 'no_termination' }, { label: 'Terminable after', value: 'terminable_after' }];
// `tour` rides along on the option so Learn Mode can spotlight a row inside the
// picker sheet. A step may only make a SheetSelect tappable if the step after it
// anchors an option — the sheet renders below the tour's blockers otherwise.
const STAKEHOLDER_SALES = ['CID REALTORS SDN BHD', "VENDOR'S SOLICITOR", "PURCHASER'S SOLICITOR"]
  .map((v, i) => ({ value: v, label: v, tour: i === 0 ? 'stakeholder-opt-cid' : undefined }));
const STAKEHOLDER_RENT = ['CID REALTORS SDN BHD', "LANDLORD'S SOLICITOR", 'LANDLORD'].map((v) => ({ value: v, label: v }));

// Parties needed before a form is ISSUED. None on the two AGREEMENTS (OTP/OTR):
// they used to demand both sides up front, which is what Leon asked us to stop
// (2026-09-04) — a buyer will not put their name on an offer the owner can read
// before accepting it, and an owner will not put theirs on an offer that has not
// been made yet. Either side may be left blank and invited to fill ITSELF in — see
// the fill & sign block on the form's detail screen.
//
// The appointment letters (ALS/ALR, ES/EL) need theirs: they are one-sided by
// nature — the owner appointing CID — so that single party IS the document. Their
// pickers carry the asterisk; the servers hold the list (PRIMARY_PARTY_BY_CODE in
// utils/bookingFormRequired) and refuse to issue without it. Since 2026-10-02 a
// DRAFT saves without it, like every other * field.

// Fields needed before ISSUING, per code — mirrors the admin portal BookingFormPage's
// `required` rules exactly (intersected with the fields this page renders), and the
// servers' utils/bookingFormRequired (TERMS_BY_CODE) holds the same list — change all
// three together. An agent-created form must clear the same bar as an admin-created one.
const REQUIRED_BY_CODE = {
  OTP: [
    ['selling_price_rm', 'Selling price'], ['balance_month', 'Balance months'], ['grace_period', 'Grace period'], ['interest_percent', 'Interest %'],
    ['vacant_possession_type', 'Vacant possession'], ['fittings_furnishings', 'Fittings & furnishings'],
    ['stakeholder', 'Stakeholder'], ['legal_matters', 'Legal matters'], ['other_terms', 'Other conditions'],
  ],
  OTR: [
    ['rental_rm', 'Monthly rental'], ['stamping_fee', 'Stamping fee'],
    ['tenancy_period', 'Tenancy period'], ['renew_year', 'Renew years'],
    ['tenancy_from', 'Tenancy from'], ['tenancy_to', 'Tenancy to'],
    ['furnish', 'Furnishings'], ['business_nature', 'Nature of business'],
    ['termination_clause_type', 'Termination clause'], ['ta_sign_date', 'TA signing deadline'],
    ['payment_type', 'Payment method'], ['stakeholder', 'Stakeholder'], ['other_terms', 'Other conditions'],
  ],
  ALS: [
    ['selling_price_rm', 'Selling price'], ['balance_month', 'Balance months'], ['grace_period', 'Grace period'], ['interest_percent', 'Interest %'],
    ['professional_fee_type', 'Professional fee'], ['vacant_possession', 'Vacant possession'],
    ['owner_capacity', 'Capacity'],
    ['appointment_basis', 'Appointment basis'], ['appointment_period', 'Appointment period'],
    ['period_type', 'Period unit'], ['commencing_from', 'Commencing from'], ['commencing_to', 'Commencing to'],
  ],
  ALR: [
    ['rental_rm', 'Monthly rental'], ['rental_sst_basis', 'Rental SST'], ['professional_fee_type', 'Professional fee'],
    ['furnish', 'Furnishings'], ['owner_capacity', 'Capacity'],
    ['appointment_basis', 'Appointment basis'], ['appointment_period', 'Appointment period'],
    ['period_type', 'Period unit'], ['commencing_from', 'Commencing from'], ['commencing_to', 'Commencing to'],
  ],
  // The exclusive letters carry no appointment BASIS — exclusive is the basis —
  // and quote their fee as separate figures rather than one free-text line. The
  // sell letter also carries a Furnishings term the general one does not.
  ES: [
    ['selling_price_rm', 'Selling price'], ['balance_month', 'Balance months'], ['grace_period', 'Grace period'], ['interest_percent', 'Interest %'],
    ['professional_fee_percent', 'Professional fee %'], ['stakeholder_earnest_percent', 'Stakeholding earnest %'], ['sst_percent', 'SST %'],
    ['vacant_possession', 'Vacant possession'], ['furnish', 'Furnishings'], ['owner_capacity', 'Capacity'],
    ['appointment_period', 'Exclusive period'],
    ['period_type', 'Period unit'], ['commencing_from', 'Commencing from'], ['commencing_to', 'Commencing to'],
  ],
  EL: [
    ['rental_rm', 'Monthly rental'], ['rental_sst_basis', 'Rental SST'],
    ['professional_fee_month', 'Professional fee (months)'], ['sst_percent', 'SST %'],
    ['furnish', 'Furnishings'], ['owner_capacity', 'Capacity'],
    ['appointment_period', 'Exclusive period'],
    ['period_type', 'Period unit'], ['commencing_from', 'Commencing from'], ['commencing_to', 'Commencing to'],
  ],
};

// What each money term is called, in the two shapes it can take. Every commercial
// figure on a booking form used to be a DERIVED one — a percentage of the selling
// price, or a multiple of the monthly rental — so a deal agreed as a flat sum
// ("RM 5,000 earnest, full stop") had to be faked as a share, which then silently
// re-prices itself the moment the price or the rent is corrected. Each term now
// carries its own basis; a form written before that carries none at all, which
// every reader treats as the derived one. See lib/bookingFormMoney.
const MONEY_LABELS = {
  earnest_deposit: { percent: 'Earnest deposit %', month: 'Earnest dep. (months)', amount: 'Earnest deposit (RM)' },
  balance_deposit: { percent: 'Balance deposit %', amount: 'Balance deposit (RM)' },
  balance_purchase_price: { percent: 'Balance purchase %', amount: 'Balance purchase (RM)' },
  advance_rental: { month: 'Advance (months)', amount: 'Advance rental (RM)' },
  security_deposit: { month: 'Security dep. (months)', amount: 'Security deposit (RM)' },
  utility_deposit: { month: 'Utility dep. (months)', amount: 'Utility deposit (RM)' },
};
const moneyLabel = (term, saleType, flat) => MONEY_LABELS[term][flat ? 'amount' : derivedBasis(saleType)];

/**
 * Mandatory fields for a code, given what is filled in so far. The conditional
 * ones live here rather than at the two call sites, so the red asterisks and the
 * submit check can never disagree about which of them applies.
 */
const requiredFor = (code, fd = {}) => {
  if (!REQUIRED_BY_CODE[code]) return [];
  const saleType = saleTypeOfCode(code);
  return [
    ...REQUIRED_BY_CODE[code],
    ...(code === 'OTR' && fd.termination_clause_type === 'terminable_after'
      ? [['termination_after_year', 'Terminable after (years)'], ['termination_notice_month', 'Termination notice (months)']]
      : []),
    // ALR/EL print an SST rate only when the rental is quoted inclusive of it.
    ...((code === 'ALR' || code === 'EL') && fd.rental_sst_basis === RENTAL_SST_INCLUSIVE
      ? [['tax_percent', 'SST % (rental)']]
      : []),
    // Every money term is required — but as the ONE figure its basis asks for.
    // Demanding the other would block a deal that was agreed the other way.
    ...moneyTerms(saleType).map((term) => {
      const { key, flat } = moneyTermField(fd, term, saleType);
      return [key, moneyLabel(term, saleType, flat)];
    }),
  ];
};

// Quick-create wiring per party-slot prefix: the registry type to create and the
// role it should default to (tenant slots → TENANT, landlord slots → LANDLORD…).
const QUICK_BY_PREFIX = {
  purchaser: { type: 'purchaser', sub: 'PURCHASER', noun: 'purchaser' },
  tenant: { type: 'purchaser', sub: 'TENANT', noun: 'tenant' },
  owner: { type: 'vendor', sub: 'VENDOR', noun: 'owner' },
  landlord: { type: 'vendor', sub: 'LANDLORD', noun: 'landlord' },
};
const quickFor = (k) => QUICK_BY_PREFIX[String(k).replace(/_\d+$/, '')] || QUICK_BY_PREFIX.owner;

// Dropdown label with creator attribution (the lookup is the caller's own contact book).
// Downline-created registries show the creator in brackets, e.g. "Ali Lee (Agent Three)".
const contactLabel = (c) => (c.creator_name && !c.is_mine ? `${c.name} (${c.creator_name})` : c.name);
// Second line on a picker row — the IC / SSM / passport that tells two similarly
// named parties apart before you commit one to a legal form.
const contactSub = (c) => [c.registration_no, c.contact_no || c.phone].filter(Boolean).join(' · ') || undefined;
const CREATE_NEW = '__create_new__';
// Party picker page size — the sheet scrolls the next page in.
const CONTACT_PAGE = 20;
// A party key holding a contact id (legacy rows sometimes hold a typed name instead).
const isUUID = (v) => typeof v === 'string' && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(v);

// Seeded values, matched to the admin BookingFormPage's `initialValues` (and the
// same set it re-applies when the form type changes). Only the two OTP text terms
// and the OTR termination shape have a house default; everything else — notably
// ALS vacant possession and the ALS/ALR period unit — is a decision the agent
// makes, so it starts empty in both apps.
const defaultsFor = (c) => {
  // Both agreements carry the owner's Letter of Undertaking unless the agent switches
  // it off (owner, 2026-10-06) — the servers default the flag too.
  if (c === 'OTP') {
    return {
      include_owner_lou: true,
      ...defaultMoneyBasis('SALES'),
      vacant_possession_type: 'vacant',
      fittings_furnishings: 'N/A',
      legal_matters: 'Both Purchaser(s) and Vendor(s) are to pay their own legal fees',
    };
  }
  if (c === 'OTR') return { include_owner_lou: true, termination_clause_type: 'terminable_after', ...defaultMoneyBasis('RENT') };
  // The exclusive letters seed nothing but their money bases. CID's printed pads
  // show a usual fee (3.24%, one month's rental), but an exclusive appointment is
  // negotiated, and a figure the agent never chose is worse than a blank.
  // Their rental is stated exclusive of SST unless the agent says otherwise —
  // wording, not a figure: most lettings attract no SST at all.
  if (c === 'ALR' || c === 'EL') return { ...defaultMoneyBasis('RENT'), rental_sst_basis: RENTAL_SST_EXCLUSIVE };
  if (c === 'ALS' || c === 'ES') return defaultMoneyBasis('SALES');
  return {};
};

// form_data keys the admin portal edits through <InputNumber> and therefore stores
// as JSON numbers. The PWA types them in a text keypad, so they are coerced once on
// the way out — otherwise the same key would be a number or a string depending on
// which app wrote the row.
const NUMERIC_KEYS = new Set([
  'selling_price_rm', 'balance_month', 'grace_period', 'interest_percent',
  'working_day', 'rental_rm', 'tax_percent', 'stamping_fee', 'tenancy_period',
  'renew_year', 'termination_after_year', 'termination_notice_month', 'appointment_period',
  // The exclusive letters' fee terms — printed as figures, so stored as numbers.
  'professional_fee_percent', 'stakeholder_earnest_percent', 'sst_percent', 'professional_fee_month',
  // The owner's Letter of Undertaking fee, signed with the agreement.
  'owner_lou_fee_percent', 'owner_lou_fee_amount_rm', 'owner_lou_disbursements_rm',
  'landlord_lou_fee_months', 'landlord_lou_disbursements_rm',
  // Both figures of every money term: a percentage, a month count and a flat sum
  // are all numbers, and which of them the form carries is the basis's business.
  ...[...SALES_MONEY_TERMS, ...RENT_MONEY_TERMS].flatMap((t) => [
    derivedKey(t, 'SALES'), derivedKey(t, 'RENT'), amountKey(t),
  ]),
]);

/** Outgoing form_data: numeric keys as numbers, blanks dropped entirely. */
const toFormData = (fd, code) => {
  const out = {};
  for (const [k, v] of Object.entries(fd || {})) {
    if (v === undefined || v === null || v === '') continue;
    if (NUMERIC_KEYS.has(k)) {
      const n = Number(v);
      if (!Number.isNaN(n)) { out[k] = n; continue; }
    }
    out[k] = v;
  }
  // Toggling a money term's basis keeps both numbers on screen, deliberately, so
  // the agent does not lose what they typed. Only the figure each basis names is
  // sent — the other would sit in form_data contradicting the printed document.
  return pruneMoneyTerms(out, saleTypeOfCode(code));
};

// Commercial terms an ALS/ALR already fixed, which the OTP/OTR it backs inherits.
// Same two lists the admin BookingFormPage carries across.
// Both figures per term: the letter holds only the one its basis named, and the
// other is simply absent.
const AUTH_CARRY_SALES = [
  'selling_price_rm', 'balance_month', 'grace_period', 'interest_percent',
  ...SALES_MONEY_TERMS.flatMap((t) => [derivedKey(t, 'SALES'), amountKey(t)]),
];
const AUTH_CARRY_RENT = [
  'rental_rm', 'tax_percent',
  ...RENT_MONEY_TERMS.flatMap((t) => [derivedKey(t, 'RENT'), amountKey(t)]),
];

/**
 * Of the values "Generate booking form" fills in, these are the ones the agent
 * may not then retype.
 *
 * They are the two facts the agreement inherits rather than negotiates: WHICH
 * property it is about, and WHO authorised its sale. The form is FK-linked to the
 * listing it was generated from and puts that listing Under Offer, so an address
 * edited away from it produces a document describing one property while holding
 * another; and the owner/landlord came off a signed ALS/ALR, so a name changed
 * here breaks the chain from the letter that authorised the deal.
 *
 * Everything else the prefill carries — price, deposits, percentages, grace
 * period, vacant possession — is a term the parties actually negotiate, so it
 * stays editable and only shows where its starting value came from. In
 * particular the agreed price routinely differs from the listing's asking price;
 * locking it would make the OTP unusable for any deal that moved off asking.
 */
const LOCKED_DERIVED_KEYS = new Set([
  'property_address',
  ...['owner', 'landlord'].flatMap((p) => ['1', '2'].flatMap((n) => [`${p}_${n}_id`, `${p}_${n}_name`, `${p}_${n}_nric`])),
]);

/**
 * Prefill an agreement from the authorisation letter signed against the same
 * property. The ALS/ALR was filled with verified owner details and negotiated
 * terms before the listing existed, so it outranks the listing's own fields —
 * this mirrors the admin BookingFormPage's fromAuthForm block exactly.
 *
 * Returns a patch; the caller decides how to merge it.
 */
const authFormPrefill = (code, authForm) => {
  const authFd = authForm?.form_data || {};
  if (!authForm || !Object.keys(authFd).length) return {};
  const isRent = code === 'OTR';
  const patch = {};

  // Owner / landlord identity — id, name and NRIC, for both party slots.
  const prefix = isRent ? 'landlord' : 'owner';
  ['1', '2'].forEach((n) => {
    ['id', 'name', 'nric'].forEach((part) => {
      const key = `${prefix}_${n}_${part}`;
      if (authFd[key]) patch[key] = authFd[key];
    });
  });

  (isRent ? AUTH_CARRY_RENT : AUTH_CARRY_SALES).forEach((k) => {
    if (authFd[k] != null && authFd[k] !== '') patch[k] = Number(authFd[k]);
  });
  // How each term was quoted carries over too — as the strings they are, outside
  // the Number() coercion above.
  moneyTerms(isRent ? 'RENT' : 'SALES').forEach((t) => {
    if (authFd[basisKey(t)]) patch[basisKey(t)] = authFd[basisKey(t)];
  });
  // Payment lines the ALR added carry over too — they are terms of the
  // same deal. Copied as objects, outside the Number() loop above.
  if (isRent) {
    const carried = extraPayments(authFd);
    if (carried.length) {
      patch.extra_payments = carried.map(({ label, basis, rm, month }) => (
        basis === 'month' ? { label, basis, month } : { label, basis, rm }
      ));
    }
  }

  // The ALS stores vacant possession as a boolean; the OTP as an enum.
  if (!isRent) {
    if (authFd.vacant_possession === true) patch.vacant_possession_type = 'vacant';
    else if (authFd.vacant_possession === false) patch.vacant_possession_type = 'existing_tenancy';
  }
  return patch;
};

export default function FormCreatePage() {
  const nav = useNavigate();
  const { id } = useParams();
  const location = useLocation();
  const { message } = AntApp.useApp();
  const editing = !!id;

  const [loaded, setLoaded] = useState(!editing);
  const [code, setCode] = useState(editing ? null : 'OTP');
  const [fd, setFormData] = useState(editing ? {} : { date: dayjs().format('YYYY-MM-DD'), ...defaultsFor('OTP') });
  // Which optional second parties are revealed.
  const [show, setShow] = useState({});
  // Server-assigned reference (loaded on edit; auto-generated on create).
  const [formRef, setFormRef] = useState(null);
  // The attached Letter of Undertaking's own number, once the agreement left DRAFT.
  const [louRef, setLouRef] = useState(null);
  // FROM LISTING (create-only) — set ONLY by arriving from a listing's "Generate
  // booking form". There is no picker: as in the admin portal, a form is linked to a
  // listing by where it was started, never by attaching one later. `listingSel` is
  // the row handed over in router state.
  const [listingSel, setListingSel] = useState(null);
  const [listingId, setListingId] = useState(null);
  // The ALS/ALR backing that listing, when it has one — named in the banner so the
  // agent knows which document the prefilled terms came from.
  const [authForm, setAuthForm] = useState(null);
  // form_data key → where "Generate booking form" got its value ('LST-00012',
  // 'ALS-00003'). Drives both the per-field provenance line and the read-only
  // state of the keys in LOCKED_DERIVED_KEYS. Empty on a hand-started form.
  const [derivedFrom, setDerivedFrom] = useState({});
  // Quick-create dialog target: { k: party slot key, ...QUICK_BY_PREFIX entry } or null.
  const [quick, setQuick] = useState(null);
  // A form past DRAFT is frozen — the same block the admin BookingFormPage renders.
  const [lockedStatus, setLockedStatus] = useState(null);
  const [lockedRaw, setLockedRaw] = useState(null);
  // Live document preview (rendered server-side from the unsaved form_data).
  const [previewHtml, setPreviewHtml] = useState(null);
  // The loaded draft's updated_at — a device copy older than it is stale.
  const [serverUpdatedAt, setServerUpdatedAt] = useState(null);

  // Every user edit goes through here, so it is also where autosave hears of it.
  const setFd = (patch) => {
    setFormData((s) => ({ ...s, ...patch }));
    autosave.noteChange();
  };

  // Quick-create result → select the new contact into the slot that asked for it.
  const onQuickCreated = (c) => {
    if (c?.id && quick?.k) setFd({ [`${quick.k}_id`]: c.id, [`${quick.k}_name`]: c.name, [`${quick.k}_nric`]: c.registration_no });
    setQuick(null);
  };

  useEffect(() => {
    if (!editing) return;
    bookingFormsApi.getBookingFormsById(id)
      .then((r) => {
        const d = r?.data ?? r;
        const data = d.form_data || {};
        setCode(d.form_type);
        // A letter saved before the rental-SST choice existed printed "inclusive",
        // and must reopen saying so rather than on a new letter's EXCLUSIVE.
        const sstUnset = (d.form_type === 'ALR' || d.form_type === 'EL') && !data.rental_sst_basis;
        setFormData(sstUnset ? { ...data, rental_sst_basis: RENTAL_SST_INCLUSIVE } : data);
        setFormRef(d.form_ref || null);
        setLouRef(d.lou_ref || null);
        setServerUpdatedAt(d.updated_at || null);
        // Only DRAFT forms are editable (the server refuses the rest). The detail
        // page only offers Edit on a draft, so this catches a deep link to
        // /forms/:id/edit — better to say so than to let the agent fill it in and
        // be refused at save.
        if (d.status_raw && d.status_raw !== 'DRAFT') { setLockedStatus(d.status || d.status_raw); setLockedRaw(d.status_raw); }
        setShow({
          purchaser_2: !!data.purchaser_2_name, owner_2: !!data.owner_2_name,
          tenant_2: !!data.tenant_2_name, landlord_2: !!data.landlord_2_name,
        });
        setLoaded(true);
      })
      .catch((e) => message.error(e.message));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // "Generate booking form" from a listing detail page (mirrors the admin portal's
  // flow): arrive with router state.fromListing → pick the matching form code
  // (RENT → OTR, else OTP), preselect the listing, seed address + price, then layer
  // the listing's authorisation letter (if it has one) on top — the ALS/ALR holds
  // verified owner details and terms that were agreed before the listing existed.
  useEffect(() => {
    const fl = location.state?.fromListing;
    if (editing || !fl?.id) return;
    const c = fl.listing_type === 'RENT' ? 'OTR' : 'OTP';
    const af = location.state?.fromAuthForm || null;
    setCode(c);
    setListingId(fl.id);
    setListingSel(fl);
    setAuthForm(af);

    const fromListing = {
      ...(fl.property_address ? { property_address: fl.property_address } : {}),
      ...(fl.asking_price_rm != null ? { [c === 'OTR' ? 'rental_rm' : 'selling_price_rm']: Number(fl.asking_price_rm) } : {}),
    };
    const fromAuth = authFormPrefill(c, af);

    setFormData((s) => ({ date: s.date, ...defaultsFor(c), ...fromListing, ...fromAuth }));

    // Provenance is recorded in the same order the values are layered, so a key
    // the letter overrode (the price, typically) is credited to the letter.
    const src = {};
    Object.keys(fromListing).forEach((k) => { src[k] = fl.listing_ref; });
    Object.keys(fromAuth).forEach((k) => { src[k] = af?.form_ref || fl.listing_ref; });
    setDerivedFrom(src);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // OTR tenancy: picking Period (years) + From auto-fills To = From + years − 1 day.
  // Only fills an empty To or re-derives a previously auto-filled one — a To loaded
  // from a saved form or typed by the agent is never overwritten.
  const autoTenancyTo = useRef(null);
  useEffect(() => {
    const yrs = Number(fd.tenancy_period);
    if (code !== 'OTR' || !yrs || !fd.tenancy_from) return;
    const to = dayjs(fd.tenancy_from).add(yrs, 'year').subtract(1, 'day').format('YYYY-MM-DD');
    if (!fd.tenancy_to || fd.tenancy_to === autoTenancyTo.current) {
      autoTenancyTo.current = to;
      // Not setFd: this follows an edit autosave has already heard about (or a
      // load), and must not by itself make a freshly opened form "unsaved".
      setFormData((s) => ({ ...s, tenancy_to: to }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [code, fd.tenancy_period, fd.tenancy_from]);

  const onCodeChange = (c) => {
    setCode(c);
    setShow({});
    autosave.noteChange();
    // Keep only the shared fields; reset the code-specific set with fresh defaults.
    setFormData((s) => ({ date: s.date, property_address: s.property_address, ...defaultsFor(c) }));
    // Backstop: unlink a selected listing when it no longer fits the code — ALS/ALR
    // never link one, OTR must link RENT, OTP must link sale-type listings. The type
    // picker is now hidden entirely once a listing is attached, so this cannot fire
    // from the UI; it stays as the guarantee behind that.
    if (listingSel && (isAuthCode(c) || (c === 'OTR') !== (listingSel.listing_type === 'RENT'))) {
      setListingId(null);
      setListingSel(null);
      setAuthForm(null);
      // Nothing is inherited any more, so nothing stays locked — the reset above
      // has already wiped the values those locks were protecting.
      setDerivedFrom({});
    }
  };

  // ── field renderers (close over fd/setFd) ────────────────────────────────
  // Requiredness (red asterisk) derives from REQUIRED_BY_CODE — the list the servers
  // enforce at issue. It no longer blocks a save: a draft may be half-finished.
  const requiredKeys = new Set([
    'date', 'property_address',
    ...requiredFor(code, fd).map(([k]) => k),
  ]);
  const isReq = (k) => requiredKeys.has(k);

  // Provenance + read-only, both derived from the one `derivedFrom` map so a field
  // can never be locked without saying which document locked it.
  const isLocked = (k) => !!derivedFrom[k] && LOCKED_DERIVED_KEYS.has(k);
  const hintFor = (k) => {
    const src = derivedFrom[k];
    if (!src) return undefined;
    return LOCKED_DERIVED_KEYS.has(k) ? `From ${src} — fixed for this form` : `Prefilled from ${src}`;
  };

  const T = (k, label, o = {}) => (
    <Field label={label} req={isReq(k)} key={k} hint={hintFor(k)}>
      {o.textarea
        ? <Input.TextArea data-tour={o.tour} value={fd[k] ?? ''} onChange={(e) => setFd({ [k]: e.target.value })} rows={o.rows || 2} placeholder={o.placeholder} disabled={isLocked(k)} />
        : <Input data-tour={o.tour} type={o.type} value={fd[k] ?? ''} onChange={(e) => setFd({ [k]: e.target.value })} placeholder={o.placeholder} suffix={o.suffix} disabled={isLocked(k)} />}
    </Field>
  );
  const D = (k, label) => (
    <Field label={label} req={isReq(k)} key={k} hint={hintFor(k)}>
      <SheetDate title={label} placeholder={`Pick ${label.toLowerCase()}`} value={fd[k] ? dayjs(fd[k]) : null} onChange={(d) => setFd({ [k]: d ? d.format('YYYY-MM-DD') : undefined })} disabled={isLocked(k)} />
    </Field>
  );
  const S = (k, label, options) => (
    <Field label={label} req={isReq(k)} key={k} hint={hintFor(k)}>
      <Segmented block value={fd[k]} onChange={(v) => setFd({ [k]: v })} options={options} disabled={isLocked(k)} />
    </Field>
  );
  const Sel = (k, label, options, ph, tour) => (
    <Field label={label} req={isReq(k)} key={k} hint={hintFor(k)}>
      <SheetSelect tour={tour} title={label} value={fd[k] ?? undefined} onChange={(v) => setFd({ [k]: v })} options={options} placeholder={ph} disabled={isLocked(k)} />
    </Field>
  );
  // The payment lines this tenancy has that the form does not print — a car-park
  // rental, an access-card deposit. Each is quoted the two ways a built-in rent
  // term is, through the same suffix toggle, and each counts into the OTR's
  // total payable. They used to go into "other terms" as prose, where the total
  // on the signed document disagreed with what the tenant actually paid.
  const extraPaymentsEditor = () => {
    const lines = Array.isArray(fd.extra_payments) ? fd.extra_payments : [];
    const patch = (i, p) => setFd({ extra_payments: lines.map((l, j) => (j === i ? { ...l, ...p } : l)) });
    return (
      <>
        {lines.map((line, i) => {
          const flat = line.basis !== 'month';
          return (
            <div key={i} style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <Field label={`Payment ${i + 1}`}>
                <Input
                  value={line.label ?? ''}
                  maxLength={EXTRA_PAYMENT_LABEL_MAX}
                  placeholder="e.g. Car park rental"
                  onChange={(e) => patch(i, { label: e.target.value })}
                />
              </Field>
              <Input
                type="number"
                value={(flat ? line.rm : line.month) ?? ''}
                placeholder={flat ? 'Amount' : 'Months'}
                onChange={(e) => patch(i, flat ? { rm: e.target.value } : { month: e.target.value })}
                suffix={(
                  <Segmented
                    size="small"
                    value={flat ? 'amount' : 'month'}
                    onChange={(v) => patch(i, { basis: v })}
                    options={moneyUnitOptions('RENT')}
                  />
                )}
              />
              <div
                style={{ ...addLink, color: 'var(--bad)' }}
                onClick={() => setFd({ extra_payments: lines.filter((_, j) => j !== i) })}
              >
                Remove
              </div>
            </div>
          );
        })}
        {lines.length < MAX_EXTRA_PAYMENTS && (
          <div style={addLink} onClick={() => setFd({ extra_payments: [...lines, blankExtraPayment()] })}>
            ＋ Add payment
          </div>
        )}
      </>
    );
  };

  // ALR/EL: is the rental quoted with SST in it? Only then is there a rate to ask.
  const rentalSst = () => (fd.rental_sst_basis === RENTAL_SST_INCLUSIVE
    ? two(Sel('rental_sst_basis', 'Rental SST', RENTAL_SST_OPTIONS, 'Select'), T('tax_percent', 'SST %', { type: 'number', suffix: '%' }))
    : Sel('rental_sst_basis', 'Rental SST', RENTAL_SST_OPTIONS, 'Select'));
  const two = (a, b) => <div style={{ display: 'flex', gap: 12 }}><div style={{ flex: 1 }}>{a}</div><div style={{ flex: 1 }}>{b}</div></div>;

  // Every party picker routes its "+ Create new…" tap here with its own slot key.
  const openQuick = (k) => setQuick({ k, ...quickFor(k) });

  // A party slot is locked by the same rule as any other field — its name key
  // came from the authorisation letter and is in LOCKED_DERIVED_KEYS.
  const partyProps = (k) => ({ locked: isLocked(`${k}_name`), hint: hintFor(`${k}_name`) });

  // On an OTP/OTR a party may be left blank, so the picker says so — under the
  // field where the decision is actually made, rather than as a banner nobody
  // reads. Provenance always wins the one hint line: "From ALS-… — fixed for this
  // form" explains a control the agent cannot use, which outranks an invitation to
  // skip it. (The letters' own party stays mandatory and keeps its asterisk.)
  const partySelfFill = (k, sideNoun) => {
    const p = partyProps(k);
    return {
      ...p,
      hint: p.hint || `Optional — leave blank and invite the ${sideNoun} to fill in their own details and sign, from the form's page.`,
    };
  };

  // Optional second party: an "+ Add" link that reveals a picker + Remove.
  const optionalParty = (key2, type, label) => {
    const on = show[key2] || !!fd[`${key2}_name`];
    if (!on) {
      return <span onClick={() => setShow((s) => ({ ...s, [key2]: true }))} style={addLink}>+ Add {label}</span>;
    }
    const p = partyProps(key2);
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        <PartyPicker k={key2} type={type} label={label} fd={fd} setFd={setFd} onCreateNew={openQuick} {...p} />
        {/* A second owner the letter named is part of that letter — removing them
            here would leave the agreement naming fewer owners than authorised it. */}
        {!(p.locked && fd[`${key2}_name`]) && (
          <span
            onClick={() => { setShow((s) => ({ ...s, [key2]: false })); setFd({ [`${key2}_id`]: undefined, [`${key2}_name`]: undefined, [`${key2}_nric`]: undefined }); }}
            style={{ cursor: 'pointer', color: 'var(--bad)', fontSize: 12.5, alignSelf: 'flex-start' }}
          >Remove {label}</span>
        )}
      </div>
    );
  };
  const witness = (nameKey, nricKey, label) => two(
    T(nameKey, `${label} name`, { placeholder: 'Full name' }),
    T(nricKey, `${label} NRIC / REN`, { placeholder: 'NRIC / REN no' }),
  );

  // POA / trustee detail fields that cascade from owner_capacity (ALS / ALR), mirroring admin.
  const capacityDetail = () => {
    if (fd.owner_capacity === 'duly authorised representative') return (
      <>
        {T('poa_date', 'POA date', { placeholder: 'DD/MM/YYYY' })}
        {two(T('poa_court', 'High Court'), T('poa_reg_no', 'Registration no'))}
      </>
    );
    if (fd.owner_capacity === 'trustee') return T('trustee_for', 'Trustee for', { placeholder: 'On behalf of' });
    return null;
  };

  // One money term, one field: the figure, with the unit it is quoted in sitting
  // inside the input itself. Six terms each with a basis picker of its own would
  // bury the numbers they qualify, so the toggle rides in the suffix slot — where
  // a plain "%" used to sit, now saying what it is and offering the alternative.
  //
  // These stay one-per-row rather than paired through `two()`: the toggle needs
  // about 100px, which in half a phone width leaves ~65px for the number — too
  // narrow to read "8,000.00" back.
  //
  // Only the figure the chosen basis names is bound, so what is marked required
  // follows the choice rather than demanding a share nobody agreed. Reading the
  // basis through `moneyBasis` rather than `fd` directly is what lets a form saved
  // before this option existed open on the basis it was written with.
  const M = (term, o = {}) => {
    const saleType = saleTypeOfCode(code);
    const { key, flat } = moneyTermField(fd, term, saleType);
    return (
      <Field label={moneyLabel(term, saleType, flat)} req={isReq(key)} key={term} hint={hintFor(key)}>
        <Input
          data-tour={o.tour}
          type="number"
          value={fd[key] ?? ''}
          placeholder={o.placeholder}
          onChange={(e) => setFd({ [key]: e.target.value })}
          suffix={(
            <Segmented
              size="small"
              value={moneyBasis(fd, term, saleType)}
              onChange={(v) => setFd({ [basisKey(term)]: v })}
              options={moneyUnitOptions(saleType)}
            />
          )}
        />
      </Field>
    );
  };

  // Sales purchase-price + deposit schedule — shared by OTP and ALS (identical form_data keys).
  const salesPaymentSchedule = () => (
    <>
      {M('earnest_deposit')}
      {M('balance_deposit')}
      {M('balance_purchase_price')}
      {T('balance_month', 'Balance months', { type: 'number' })}
      {two(T('grace_period', 'Grace period (months)', { type: 'number' }), T('interest_percent', 'Interest % p.a.', { type: 'number', suffix: '%' }))}
    </>
  );

  // ── per-code field sets ──────────────────────────────────────────────────
  const renderByCode = () => {
    if (code === 'OTP') return (
      <>
        <div style={sectionLabel}>PURCHASER(S)</div>
        <div style={card}>
          <div data-tour="form-purchaser-1">
            <PartyPicker tour="party-purchaser" k="purchaser_1" type="PURCHASER" label="Purchaser 1" fd={fd} setFd={setFd} onCreateNew={openQuick} {...partySelfFill('purchaser_1', 'purchaser')} />
          </div>
          {optionalParty('purchaser_2', 'PURCHASER', 'Purchaser 2')}
          {witness('purchaser_witness_name', 'purchaser_witness_nric', 'Purchaser witness')}
        </div>

        <div style={sectionLabel}>VENDOR / OWNER(S)</div>
        <div style={card} data-tour="form-owner-1">
          <PartyPicker tour="party-owner" k="owner_1" type="VENDOR" label="Owner 1" fd={fd} setFd={setFd} onCreateNew={openQuick} {...partySelfFill('owner_1', 'vendor / owner')} />
          {optionalParty('owner_2', 'VENDOR', 'Owner 2')}
          {witness('owner_witness_name', 'owner_witness_nric', 'Vendor witness')}
        </div>

        <div style={sectionLabel}>PURCHASE PRICE & DEPOSITS</div>
        <div style={card} data-tour="form-money-card">
          {T('selling_price_rm', 'Selling price (RM)', { type: 'number', placeholder: '1250000', tour: 'form-price' })}
          {M('earnest_deposit', { tour: 'form-earnest' })}
          {M('balance_deposit', { tour: 'form-bal-dep' })}
          {M('balance_purchase_price', { tour: 'form-bal-pur' })}
          {T('balance_month', 'Balance months', { type: 'number', tour: 'form-bal-mth' })}
          {two(T('grace_period', 'Grace period (months)', { type: 'number', tour: 'form-grace' }), T('interest_percent', 'Interest % p.a.', { type: 'number', suffix: '%', tour: 'form-interest' }))}
        </div>

        <div style={sectionLabel}>TERMS</div>
        <div style={card}>
          {S('vacant_possession_type', 'Vacant possession', VACANT_TYPE_OPTS)}
          {T('working_day', 'SPA signing deadline (days)', { type: 'number', placeholder: 'e.g. 14' })}
          {T('fittings_furnishings', 'Fittings & furnishings', { placeholder: '(if any)' })}
          {two(T('payment_type', 'Payment method', { placeholder: 'Cash / Transfer' }), T('payment_ref_no', 'Payment ref / cheque no'))}
          {Sel('stakeholder', 'Stakeholder', STAKEHOLDER_SALES, 'Select stakeholder', 'form-stakeholder')}
          {T('legal_matters', 'Legal matters', { textarea: true, placeholder: 'e.g. each party pays their own legal fees' })}
          {T('other_terms', 'Other conditions', { textarea: true, rows: 3, tour: 'form-other-terms' })}
        </div>

        <InventorySection fd={fd} setFd={setFd} />
        <OwnerLouSection fd={fd} setFd={setFd} formRef={formRef} louRef={louRef} />
      </>
    );

    if (code === 'OTR') return (
      <>
        <div style={sectionLabel}>TENANT(S)</div>
        <div style={card}>
          <PartyPicker k="tenant_1" type="PURCHASER" label="Tenant 1" fd={fd} setFd={setFd} onCreateNew={openQuick} {...partySelfFill('tenant_1', 'tenant')} />
          {optionalParty('tenant_2', 'PURCHASER', 'Tenant 2')}
          {witness('tenant_witness_name', 'tenant_witness_nric', 'Tenant witness')}
        </div>

        <div style={sectionLabel}>LANDLORD(S)</div>
        <div style={card}>
          <PartyPicker k="landlord_1" type="VENDOR" label="Landlord 1" fd={fd} setFd={setFd} onCreateNew={openQuick} {...partySelfFill('landlord_1', 'landlord')} />
          {optionalParty('landlord_2', 'VENDOR', 'Landlord 2')}
          {witness('landlord_witness_name', 'landlord_witness_nric', 'Landlord witness')}
        </div>

        <div style={sectionLabel}>RENTAL & DEPOSITS</div>
        <div style={card}>
          {T('rental_rm', 'Monthly rental (RM)', { type: 'number', placeholder: '3500' })}
          {M('advance_rental')}
          {M('security_deposit')}
          {M('utility_deposit')}
          {M('earnest_deposit')}
          {/* The stamping fee has always been a flat sum — nothing to derive it from. */}
          {T('stamping_fee', 'Stamping fee (RM)', { type: 'number' })}
          {extraPaymentsEditor()}
        </div>

        <div style={sectionLabel}>TENANCY PERIOD</div>
        <div style={card}>
          {two(T('tenancy_period', 'Period (years)', { type: 'number' }), T('renew_year', 'Renew (years)', { type: 'number' }))}
          {two(D('tenancy_from', 'From'), D('tenancy_to', 'To'))}
        </div>

        <div style={sectionLabel}>TERMS</div>
        <div style={card}>
          {Sel('furnish', 'Furnishings', FURNISH_OPTS, 'Select')}
          {T('business_nature', 'Nature of business', { placeholder: 'For commercial properties' })}
          {S('termination_clause_type', 'Termination clause', TERMINATION_OPTS)}
          {fd.termination_clause_type === 'terminable_after' && two(
            T('termination_after_year', 'Terminable after (years)', { type: 'number' }),
            T('termination_notice_month', 'Notice (months)', { type: 'number' }),
          )}
          {D('ta_sign_date', 'TA signing deadline')}
          {two(T('payment_type', 'Payment method', { placeholder: 'Cash / Transfer' }), T('payment_ref_no', 'Payment ref / cheque no'))}
          {Sel('stakeholder', 'Stakeholder', STAKEHOLDER_RENT, 'Select stakeholder')}
          {T('other_terms', 'Other conditions', { textarea: true, rows: 3, tour: 'form-other-terms' })}
        </div>

        <InventorySection fd={fd} setFd={setFd} rent />
        <OwnerLouSection fd={fd} setFd={setFd} rent formRef={formRef} louRef={louRef} />
      </>
    );

    if (code === 'ALS') return (
      <>
        <div style={sectionLabel}>VENDOR / OWNER(S)</div>
        <div style={card}>
          <PartyPicker req k="owner_1" type="VENDOR" label="Owner 1" fd={fd} setFd={setFd} onCreateNew={openQuick} />
          {optionalParty('owner_2', 'VENDOR', 'Owner 2')}
          {witness('owner_witness_1_name', 'owner_witness_1_nric', 'Witness')}
        </div>

        <div style={sectionLabel}>PRICE & PAYMENT SCHEDULE</div>
        <div style={card}>
          {T('selling_price_rm', 'Selling price (RM)', { type: 'number', placeholder: '1250000' })}
          {salesPaymentSchedule()}
        </div>

        <div style={sectionLabel}>FEE & AUTHORISATION</div>
        <div style={card}>
          {T('professional_fee_type', 'Professional fee', { placeholder: 'e.g. 2% exclusive 8% SST' })}
          {Sel('owner_capacity', 'Capacity', CAPACITY_OPTS, 'Select')}
          {capacityDetail()}
          {/* Prints verbatim on the letter as YES / NO — no default, the owner decides. */}
          {Sel('vacant_possession', 'Vacant possession', VACANT_YN_OPTS, 'Select YES or NO')}
        </div>

        <div style={sectionLabel}>APPOINTMENT</div>
        <div style={card}>
          {Sel('appointment_basis', 'Appointment basis', BASIS_OPTS, 'Select')}
          {/* A picker, not a Segmented: the appointment unit has no house default (as in
              the admin portal), and a Segmented would show "Days" pre-selected while the
              value is still unset. */}
          {two(T('appointment_period', 'Period', { type: 'number' }), Sel('period_type', 'Unit', PERIOD_TYPE_OPTS, 'Select'))}
          {two(D('commencing_from', 'From'), D('commencing_to', 'To'))}
          {T('remark', 'Remark', { textarea: true, rows: 3 })}
        </div>
      </>
    );

    if (code === 'ALR') return (
      <>
        <div style={sectionLabel}>LANDLORD(S)</div>
        <div style={card}>
          <PartyPicker req k="landlord_1" type="VENDOR" label="Landlord 1" fd={fd} setFd={setFd} onCreateNew={openQuick} />
          {optionalParty('landlord_2', 'VENDOR', 'Landlord 2')}
          {witness('landlord_witness_1_name', 'landlord_witness_1_nric', 'Witness')}
        </div>

        <div style={sectionLabel}>RENTAL & DEPOSITS</div>
        <div style={card}>
          {T('rental_rm', 'Monthly rental (RM)', { type: 'number', placeholder: '3500' })}
          {rentalSst()}
          {M('advance_rental')}
          {M('security_deposit')}
          {M('utility_deposit')}
          {M('earnest_deposit')}
          {extraPaymentsEditor()}
          {T('professional_fee_type', 'Professional fee', { placeholder: 'e.g. 1 month rental exclusive 8% SST' })}
        </div>

        <div style={sectionLabel}>APPOINTMENT</div>
        <div style={card}>
          {Sel('appointment_basis', 'Appointment basis', BASIS_OPTS, 'Select')}
          {/* A picker, not a Segmented: the appointment unit has no house default (as in
              the admin portal), and a Segmented would show "Days" pre-selected while the
              value is still unset. */}
          {two(T('appointment_period', 'Period', { type: 'number' }), Sel('period_type', 'Unit', PERIOD_TYPE_OPTS, 'Select'))}
          {two(D('commencing_from', 'From'), D('commencing_to', 'To'))}
          {Sel('furnish', 'Furnishings', FURNISH_OPTS, 'Select')}
          {Sel('owner_capacity', 'Capacity', CAPACITY_OPTS, 'Select')}
          {capacityDetail()}
          {T('remark', 'Remark', { textarea: true, rows: 3 })}
        </div>
      </>
    );

    // ES — the exclusive twin of the ALS. Same parties, same payment schedule;
    // what differs is that exclusivity IS the basis (so no basis picker), the fee
    // is three separate figures rather than a free-text line, and the pad carries
    // a Furnishings term plus an address and SST ID under the signature.
    if (code === 'ES') return (
      <>
        <div style={sectionLabel}>VENDOR / OWNER(S)</div>
        <div style={card}>
          <PartyPicker req k="owner_1" type="VENDOR" label="Owner 1" fd={fd} setFd={setFd} onCreateNew={openQuick} />
          {optionalParty('owner_2', 'VENDOR', 'Owner 2')}
          {witness('owner_witness_1_name', 'owner_witness_1_nric', 'Witness')}
        </div>

        <div style={sectionLabel}>PRICE & PAYMENT SCHEDULE</div>
        <div style={card}>
          {T('selling_price_rm', 'Selling price (RM)', { type: 'number', placeholder: '1250000' })}
          {salesPaymentSchedule()}
        </div>

        <div style={sectionLabel}>FEE & AUTHORISATION</div>
        <div style={card}>
          {two(
            T('professional_fee_percent', 'Professional fee %', { type: 'number', suffix: '%' }),
            T('sst_percent', 'SST %', { type: 'number', suffix: '%' }),
          )}
          {/* Clause 8's stakeholding earnest is its own figure: on CID's printed
              pad it differs from the earnest in the payment terms above. */}
          {T('stakeholder_earnest_percent', 'Stakeholding earnest %', { type: 'number', suffix: '%' })}
          {Sel('owner_capacity', 'Capacity', CAPACITY_OPTS, 'Select')}
          {capacityDetail()}
          {/* Prints verbatim on the letter as YES / NO — no default, the owner decides. */}
          {Sel('vacant_possession', 'Vacant possession', VACANT_YN_OPTS, 'Select YES or NO')}
          {Sel('furnish', 'Furnishings', FURNISH_OPTS, 'Select')}
        </div>

        <div style={sectionLabel}>EXCLUSIVE PERIOD</div>
        <div style={card}>
          {two(T('appointment_period', 'Period', { type: 'number' }), Sel('period_type', 'Unit', PERIOD_TYPE_OPTS, 'Select'))}
          {two(D('commencing_from', 'From'), D('commencing_to', 'To'))}
          {/* Printed under the signature; blank is fine — it can be written in
              by hand at signing, as it is on the paper pad. */}
          {T('owner_address', 'Vendor address (signature block)')}
          {T('owner_sst_id', 'Vendor SST ID (signature block)')}
          {T('remark', 'Remark', { textarea: true, rows: 3 })}
        </div>
      </>
    );

    // EL — the exclusive twin of the ALR. The fee is months of rental plus an SST
    // rate rather than one free-text line, and there is no basis to choose.
    if (code === 'EL') return (
      <>
        <div style={sectionLabel}>LANDLORD(S)</div>
        <div style={card}>
          <PartyPicker req k="landlord_1" type="VENDOR" label="Landlord 1" fd={fd} setFd={setFd} onCreateNew={openQuick} />
          {optionalParty('landlord_2', 'VENDOR', 'Landlord 2')}
          {witness('landlord_witness_1_name', 'landlord_witness_1_nric', 'Witness')}
        </div>

        <div style={sectionLabel}>RENTAL & DEPOSITS</div>
        <div style={card}>
          {T('rental_rm', 'Monthly rental (RM)', { type: 'number', placeholder: '3500' })}
          {rentalSst()}
          {M('advance_rental')}
          {M('security_deposit')}
          {M('utility_deposit')}
          {M('earnest_deposit')}
          {extraPaymentsEditor()}
          {two(
            T('professional_fee_month', 'Professional fee (months)', { type: 'number' }),
            T('sst_percent', 'SST % on fee', { type: 'number', suffix: '%' }),
          )}
        </div>

        <div style={sectionLabel}>EXCLUSIVE PERIOD</div>
        <div style={card}>
          {two(T('appointment_period', 'Period', { type: 'number' }), Sel('period_type', 'Unit', PERIOD_TYPE_OPTS, 'Select'))}
          {two(D('commencing_from', 'From'), D('commencing_to', 'To'))}
          {Sel('furnish', 'Furnishings', FURNISH_OPTS, 'Select')}
          {Sel('owner_capacity', 'Capacity', CAPACITY_OPTS, 'Select')}
          {capacityDetail()}
          {/* Printed under the signature; blank is fine — it can be written in
              by hand at signing, as it is on the paper pad. */}
          {T('landlord_address', 'Landlord address (signature block)')}
          {T('remark', 'Remark', { textarea: true, rows: 3 })}
        </div>
      </>
    );

    return null;
  };

  // PREVIEW — render the unsaved form_data through the same Handlebars template the
  // signed document uses, so the agent reads the actual letter before creating it.
  // The admin portal keeps this live in a split pane; a phone has no room for one,
  // so it opens full-screen on demand through the shared DocViewer.
  const openPreview = async () => {
    if (!code) { message.warning('Pick a form type first'); return; }
    const r = await bookingFormsApi.createBookingFormsPreview({
      form_type: code,
      form_data: toFormData(fd, code),
      form_ref: formRef || 'PREVIEW',
    });
    const html = (r?.data ?? r)?.html;
    if (!html) throw new Error('Could not render the preview');
    setPreviewHtml(html);
  };

  // Autosave, the owner's "C" (2026-10-02): a new form is kept on this device
  // until Save draft; a saved draft (edit is DRAFT-only) saves itself to the
  // server. Same keys as the desktop FormCreate, so rotating an iPad across the
  // 1100px switch brings the typing across.
  const { agent } = useAuth() || {};
  const [loadedTick, setLoadedTick] = useState(0);
  useEffect(() => { if (loaded) setLoadedTick((t) => t + 1); }, [loaded]);
  const fromListingId = location.state?.fromListing?.id;
  const autosave = useFormAutosave({
    draftKey: `booking-form:${id || `new${!editing && fromListingId ? `:listing:${fromListingId}` : ''}`}`,
    userId: agent?.id,
    readValues: () => fd,
    readMeta: () => ({ code, show, derivedFrom }),
    writeValues: (values, meta) => {
      if (meta.code !== undefined) setCode(meta.code);
      setShow(meta.show || {});
      setDerivedFrom(meta.derivedFrom || {});
      setFormData(values || {});
    },
    serverSave: editing && loaded && !lockedStatus
      ? () => bookingFormsApi.updateBookingFormsById(id, { form_data: toFormData(fd, code) })
      : undefined,
    serverUpdatedAt: serverUpdatedAt,
    loadedTick,
  });

  /** Save as a draft. Returns the saved form, or null (having said why). Throws on an API error. */
  const saveDraft = async () => {
    if (!code) { message.warning('Pick a form type first'); return null; }

    // A draft is work in progress and saves with any term or party still blank
    // (Komala, 2026-09-17: a half-keyed OTP could not be put down and finished
    // later). The red asterisks mark what is needed before ISSUING — both servers
    // hold that list (utils/bookingFormRequired, mirroring the party pickers'
    // asterisks and requiredFor above) and refuse the issue, and any signature, while it is open.
    // Only a mistake that is wrong at every stage still blocks the save:

    // Second party can never be the same person as the first (mirrors admin).
    for (const p of ['purchaser', 'owner', 'tenant', 'landlord']) {
      if (fd[`${p}_2_id`] && fd[`${p}_2_id`] === fd[`${p}_1_id`]) {
        message.warning(`Second ${p} cannot be the same person as the first`);
        return null;
      }
    }

    const form_data = toFormData(fd, code);
    const token = autosave.changeToken();
    const r = editing
      ? await bookingFormsApi.updateBookingFormsById(id, { form_data })
      : await bookingFormsApi.createBookingForms({ form_type: code, form_data, ...(listingId ? { listing_id: listingId } : {}) });
    autosave.markSaved(token);
    return (r?.data ?? r) || { id };
  };

  const guard = useUnsavedChanges(autosave.dirty, {
    what: `this ${code || 'booking form'}`,
    keptOnDevice: !autosave.toServer,
    onSave: async () => {
      try {
        return !!(await saveDraft());
      } catch (e) {
        message.error(e.message);
        return false;
      }
    },
    saveLabel: 'Save draft & leave',
    onDiscard: autosave.discardDevice,
  });

  const submit = async () => {
    const saved = await saveDraft();
    if (!saved) return;
    message.success(editing ? 'Draft saved' : 'Draft created');
    guard.release();
    nav(saved.id ? `/forms/${saved.id}` : '/forms');
  };

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <AppBar back title={editing ? 'Edit booking form' : 'New booking form'} sub={code ? CODE_LABELS[code] : 'pick a form type'} />

      <div className="scrx" style={{ flex: 1, overflowY: 'auto', padding: 18, display: 'flex', flexDirection: 'column', gap: 14 }}>
        {editing && !loaded ? (
          <div style={{ color: 'var(--muted)', fontSize: 13 }}>Loading…</div>
        ) : lockedStatus ? (
          <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 14, padding: 18, display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div style={{ fontSize: 13.5, fontWeight: 600 }}>
              This form is {String(lockedStatus).toLowerCase()}{lockedEditHint(lockedRaw) ? ', so it is not edited here.' : ' and cannot be edited.'}
            </div>
            {lockedEditHint(lockedRaw) && (
              <div style={{ fontSize: 13, color: 'var(--muted)' }}>{lockedEditHint(lockedRaw)}</div>
            )}
            <AsyncButton block onClick={async () => nav(`/forms/${id}`)}>Back to the form</AsyncButton>
          </div>
        ) : (
          <>
            <RestoredDraftBanner autosave={autosave} onServer={editing} notYet="It is not a draft yet — Save draft keeps it." />
            {/* Form code — a picker only on a form started from scratch. Generating
                from a listing decides it (RENT → OTR, everything else → OTP), and
                changing it afterwards would mean the agreement no longer matches the
                property it is linked to, so it reads as a stated fact instead.

                When it IS a choice: six codes in a phone-width Segmented leaves each
                about three characters wide, so the chosen code's full name goes
                underneath as the field's hint — ES and EL are new, and nobody should
                have to guess which of them is the sell letter. */}
            <div style={card} data-tour="form-type">
              {editing || listingSel ? (
                <Field
                  label="Form type"
                  hint={!editing && listingSel
                    ? `Set by listing ${listingSel.listing_ref} — a ${listingSel.listing_type === 'RENT' ? 'rental' : 'sale'} listing takes ${code === 'OTR' ? 'an OTR' : 'an OTP'}`
                    : undefined}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <span style={{ fontSize: 15, fontWeight: 700 }}>{code}</span>
                    <span style={{ fontSize: 12.5, color: 'var(--muted)' }}>{CODE_LABELS[code]}</span>
                  </div>
                </Field>
              ) : (
                <Field label="Form type" hint={code ? CODE_LABELS[code] : undefined}>
                  <Segmented block value={code} onChange={onCodeChange} options={CODE_OPTS} />
                </Field>
              )}
            </div>

            {/* FROM LISTING — there is no listing picker here, matching the admin
                portal: a form is linked to a listing only by being started from that
                listing ("Generate booking form" on the listing page), never by
                attaching one afterwards. This banner is that entry point's receipt,
                and mirrors the Alert the admin BookingFormPage shows. */}
            {!editing && listingSel && (
              <div data-tour="form-from-listing-banner" style={{ background: 'var(--chip)', borderRadius: 12, padding: '14px 15px', fontSize: 11.5, color: 'var(--primary)', lineHeight: 1.55 }}>
                <b>Generating from listing {listingSel.listing_ref}.</b>{' '}
                {authForm?.form_ref
                  ? `The property and the owner details come from authorisation letter ${authForm.form_ref} and are fixed for this form. The price and terms are pre-filled from it too — change them if the deal was negotiated differently.`
                  : 'The property address comes from the listing and is fixed for this form. The price is pre-filled from the asking price — change it if the deal was negotiated differently.'}
                {' '}This form is linked to the listing — once approved, the listing
                becomes Under Offer.
              </div>
            )}

            {/* Common fields */}
            <div style={card}>
              {D('date', 'Date')}
              {T('property_address', 'Property address', { textarea: true, placeholder: 'Full property address', tour: 'form-address' })}
            </div>

            {code && renderByCode()}

            {/* Auto reference — assigned server-side on save; real ref when editing */}
            <div style={{ background: 'var(--chip)', borderRadius: 12, padding: '14px 15px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
              <span style={{ fontSize: 12.5, color: 'var(--primary)', fontWeight: 500 }}>Reference (auto)</span>
              <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--primary)', fontFamily: 'ui-monospace,monospace', whiteSpace: 'nowrap' }}>
                {editing ? (formRef || '—') : `${code || 'BF'}-${new Date().getFullYear()}····`}
              </span>
            </div>
          </>
        )}
      </div>

      <PartyQuickCreate open={!!quick} type={quick?.type} defaultSub={quick?.sub} onClose={() => setQuick(null)} onCreated={onQuickCreated} />

      {!lockedStatus && (
        <div style={{ padding: '10px 12px max(12px, env(safe-area-inset-bottom))', borderTop: '1px solid var(--border)', background: 'var(--surface)', flex: 'none', display: 'flex', flexDirection: 'column', gap: 8 }}>
          <AutosaveStatus
            autosave={autosave}
            idleText="Save any time — * fields are needed before issuing"
            savedLabel={`Saved to draft ${formRef || ''}`}
            style={{ textAlign: 'center' }}
          />
          <div style={{ display: 'flex', gap: 10 }}>
            <AsyncButton onClick={openPreview} onError={(e) => message.error(e.message)} style={{ flex: 'none', paddingInline: 18 }}>
              Preview
            </AsyncButton>
            <AsyncButton data-tour="form-save" type="primary" block onClick={submit} onError={(e) => message.error(e.message)}>
              {editing ? 'Save changes' : 'Save draft'}
            </AsyncButton>
          </div>
        </div>
      )}

      {/* The document as it will be signed, rendered from what's on screen right now. */}
      {previewHtml && (
        <DocViewer
          title={`Preview · ${formRef || code || 'Booking form'}`}
          html={previewHtml}
          onClose={() => setPreviewHtml(null)}
        />
      )}
    </div>
  );
}

// Single-party typeahead → picks a contact and writes the 3 canonical keys
// (${k}_id / ${k}_name / ${k}_nric). Shows the picked party as text + "Change".
// The lookup is the caller's own contact book (creator shown in the label)
// and a pinned "+ Create new…" entry opens the quick-create dialog for this slot.
//
// "Edit details" (owner 2026-11-02, Leon Lim): fixes the picked contact itself — a
// wrong name or a "TBC" SSM number — and refreshes this form's own copy of the name
// and number, which is what prints. "Change" swaps in a different party instead.
// Offered on a party inherited from an authorisation letter too: it is the same
// contact, corrected, not a different one. Not on a legacy row that holds a typed
// name with no contact behind it.
function PartyPicker({ k, type, label, req, fd, setFd, onCreateNew, locked, hint, tour }) {
  const { message } = AntApp.useApp();
  const idKey = `${k}_id`, nameKey = `${k}_name`, nricKey = `${k}_nric`;
  const picked = fd[nameKey];
  const { type: kind, noun } = quickFor(k);
  // The record stays set after the sheet closes, so it slides out as the edit sheet
  // it was rather than flashing back to a blank "New …" form; the next open replaces it.
  const [editRec, setEditRec] = useState(null);
  const [editOpen, setEditOpen] = useState(false);
  const canEditDetails = isUUID(fd[idKey]);
  const openEdit = async () => {
    try {
      const r = await (kind === 'purchaser' ? contactsApi.getPurchasersById : contactsApi.getVendorsById)(fd[idKey]);
      const d = r?.data ?? r;
      if (d?.can_edit === false) { message.info('This contact can only be changed by HQ.'); return; }
      setEditRec(d);
      setEditOpen(true);
    } catch (e) { message.error(e.message); }
  };
  const editSheet = (
    <PartyQuickCreate
      open={editOpen}
      type={kind}
      record={editRec}
      noun={noun}
      onClose={() => setEditOpen(false)}
      onCreated={(c) => {
        // Only a real change to what the form holds is a change to the form — a save
        // with nothing edited must not mark it unsaved or start an autosave.
        if (c?.id && (c.id !== fd[idKey] || (c.name || '') !== (fd[nameKey] || '') || (c.registration_no || '') !== (fd[nricKey] || ''))) {
          setFd({ [idKey]: c.id, [nameKey]: c.name, [nricKey]: c.registration_no });
        }
        setEditOpen(false);
      }}
    />
  );
  const editLink = canEditDetails && (
    <span onClick={openEdit} style={{ cursor: 'pointer', color: 'var(--primary)', fontSize: 12.5, fontWeight: 600, flex: 'none' }}>Edit details</span>
  );
  // The picked party: the name gets the card's full width and wraps (two lines at
  // most), with Edit details / Change on their own row under the number — beside the
  // name they cut a long company name to a dozen characters on a phone.
  const changeLink = (
    <span onClick={() => setFd({ [idKey]: undefined, [nameKey]: undefined, [nricKey]: undefined })} style={{ cursor: 'pointer', color: 'var(--primary)', fontSize: 12.5, fontWeight: 600, flex: 'none' }}>Change</span>
  );
  const pickedCard = (isLocked) => (
    <Field label={label} req={req} hint={hint}>
      <div style={{ border: '1px solid var(--border)', borderRadius: 10, padding: '13px 14px', ...(isLocked ? { background: 'var(--surface2)' } : {}) }}>
        <div title={picked} style={{ fontSize: 13, fontWeight: 600, lineHeight: 1.35, overflowWrap: 'anywhere', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{picked}</div>
        {fd[nricKey] && <div style={{ fontSize: 11.5, color: 'var(--muted)', marginTop: 2 }}>{fd[nricKey]}</div>}
        {(editLink || !isLocked) && (
          <div style={{ display: 'flex', gap: 18, marginTop: 8 }}>
            {editLink}
            {!isLocked && changeLink}
          </div>
        )}
      </div>
      {editSheet}
    </Field>
  );

  // Paged: the caller's own book runs past one page, so a single capped page hid
  // real contacts behind a list that simply stopped at 20 with nothing more to load.
  // Called before any early return below: `locked` and `picked` both arrive after
  // mount (the prefill is applied in an effect), so a return above this hook would
  // change the hook count between renders.
  const lookup = usePagedOptions(async (q, page) => {
    const r = await contactsApi.listContactsLookup({ type, q, page, limit: CONTACT_PAGE });
    return (r?.data ?? r ?? []).map((c) => ({ value: c.id, label: contactLabel(c), sub: contactSub(c), data: c }));
  }, CONTACT_PAGE);

  // Inherited from a signed authorisation letter: shown as the value it is, with
  // no way to swap the party out. Falls through to the pickers below if the
  // letter somehow named nobody, so a locked-but-empty slot is still fillable.
  if (locked && picked) return pickedCard(true);
  if (picked) return pickedCard(false);

  return (
    <Field label={label} req={req} hint={hint}>
      <SheetSelect
        tour={tour && `${tour}-trigger`}
        title={`Pick ${label.toLowerCase()}`}
        placeholder="Search & pick…"
        searchPlaceholder={`Search ${noun}s by name or IC…`}
        value={null}
        onSearch={lookup.search}
        onChange={(cid) => {
          if (cid === CREATE_NEW) { onCreateNew?.(k); return; }
          const d = lookup.options.find((o) => o.value === cid)?.data;
          if (d) setFd({ [idKey]: d.id, [nameKey]: d.name, [nricKey]: d.registration_no });
        }}
        options={onCreateNew
          ? [{ value: CREATE_NEW, tour: tour && `${tour}-create-new`, label: <span style={{ color: 'var(--primary)', fontWeight: 600 }}>＋ Create new {noun}…</span> }, ...lookup.options]
          : lookup.options}
        onLoadMore={lookup.loadMore}
        hasMore={lookup.hasMore}
        loadingMore={lookup.loadingMore}
        notFoundContent={`No ${noun} matches — use ＋ Create new above.`}
      />
    </Field>
  );
}

const card = { background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 14, padding: 16, display: 'flex', flexDirection: 'column', gap: 14 };
const sectionLabel = { fontSize: 11.5, fontWeight: 600, letterSpacing: '.08em', color: 'var(--muted)', marginBottom: -6, marginTop: 2 };
const addLink = { cursor: 'pointer', color: 'var(--primary)', fontSize: 12.5, fontWeight: 600, alignSelf: 'flex-start' };
// `req` renders the admin-portal-style red asterisk on mandatory fields.
// `hint` is the one-line provenance note under a value that came from somewhere
// else — the listing, or the authorisation letter behind it.
const Field = ({ label, req, hint, children }) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
    <label style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--muted)' }}>
      {req && <span style={{ color: 'var(--bad)', marginRight: 3 }}>*</span>}{label}
    </label>
    {children}
    {hint && <div style={{ fontSize: 11.5, color: 'var(--muted)', lineHeight: 1.4 }}>{hint}</div>}
  </div>
);
